import { DebugLog } from "../debug/log";
import type { Idea, Persona, Settings } from "../data/types";
import type { ModelInfo } from "../data/modelStore";
import { getActiveEngine } from "./active";
import {
  PERSONAS_SYSTEM_PROMPT,
  buildPersonasUserMessage,
  extractFirstJson,
} from "./prompts";

const TAG = "engine.personas";

interface RawPersona {
  name?: string;
  descriptor?: string;
  voice_signature?: string;
  evidence_source?: string;
  jobs_to_be_done?: string;
  current_alternative?: string;
  dealbreakers?: string[];
  must_haves?: string[];
}

export type PersonasOutcome =
  | { kind: "success"; personas: Persona[] }
  | { kind: "error"; message: string };

export async function generatePersonas(
  idea: Idea,
  settings: Settings,
  installedModel: ModelInfo | null,
): Promise<PersonasOutcome> {
  const engine = getActiveEngine(settings, installedModel);
  if (!engine) return { kind: "error", message: "No engine configured." };
  DebugLog.i(TAG, `personas starting for idea ${idea.id}`);
  const result = await engine.complete({
    messages: [
      { role: "system", content: PERSONAS_SYSTEM_PROMPT },
      { role: "user", content: buildPersonasUserMessage(idea) },
    ],
    maxTokens: 2048,
    temperature: 0.6,
  });
  if (result.kind === "error") return { kind: "error", message: result.message };
  DebugLog.i(TAG, `personas reply (${result.text.length} chars)`, result.text.slice(0, 300));
  const parsed = extractFirstJson<RawPersona[]>(result.text);
  if (!parsed || !Array.isArray(parsed) || parsed.length === 0) {
    return {
      kind: "error",
      message: "Engine didn't return a parseable JSON array of personas.",
    };
  }
  const now = Date.now();
  const personas: Persona[] = parsed
    .map((raw, idx) => ({
      id: `persona-${now}-${idx}`,
      name: String(raw.name ?? `Persona ${idx + 1}`).trim(),
      descriptor: String(raw.descriptor ?? "").trim(),
      voiceSignature: String(raw.voice_signature ?? "").trim(),
      evidenceSource: String(raw.evidence_source ?? "").trim(),
      jobsToBeDone: String(raw.jobs_to_be_done ?? "").trim(),
      currentAlternative: String(raw.current_alternative ?? "").trim(),
      dealbreakers: Array.isArray(raw.dealbreakers)
        ? raw.dealbreakers.map(String)
        : [],
      mustHaves: Array.isArray(raw.must_haves) ? raw.must_haves.map(String) : [],
    }))
    .filter((p) => p.name.length > 0);
  return { kind: "success", personas };
}
